import GalleryHeader from "@components/GalleryHeader/GalleryHeader";
import { Flex, Checkbox, Image, Modal } from "antd";
import { useState, useEffect } from "react";
import "./GalleryContent.scss";
import { EyeOutlined } from "@ant-design/icons";
import { GalleryApi } from "services/GalleryApi";
import Validates from "@utils/Validates";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
interface IGalleryContent {
  type: string;
  data: any[];
  setData: (type: string, data: string[]) => void;
  selectedImg: any[];
  setSelectedImg: (selected: any[]) => void;
  isSelectedAll: boolean;
  setIsSelectedAll: (isSelected: boolean) => void;
  limit: number;
}

const GalleryContent: React.FC<IGalleryContent> = ({
  type,
  data,
  setData,
  selectedImg,
  setSelectedImg,
  isSelectedAll,
  setIsSelectedAll,
  limit,
}) => {
  const [videoPreview, setVideoPreview] = useState("");
  const [isModalVideoPreview, setIsModalVideoPreview] = useState(false);
  const [placeHolderQuantity, setPlaceHolderQuantity] = useState(0);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);

  const handleAddSelected = (image: any, checked: boolean) => {
    if (checked) {
      setSelectedImg([...selectedImg, image]);
    } else {
      const tmpArr = [...selectedImg];
      const filteredArr = tmpArr.filter((item) => item != image);
      setSelectedImg(filteredArr);
    }
  };

  useEffect(() => {
    if (isSelectedAll) {
      const images = [...data];
      setSelectedImg(images);
    } else {
      setSelectedImg([]);
    }
  }, [isSelectedAll]);

  const handleDelete = async () => {
    const tmpData = [...data];
    const selectedImgIds = selectedImg
      .filter(
        (item) => item.is_default == false || item.is_default == undefined
      )
      .map((item) => item.documentId);
    const updatedData = tmpData.filter(
      (item) =>
        !selectedImg.filter((ite) => ite.is_default != true).includes(item)
    );
    setSelectedImg([]);
    setIsOpenConfirm(false);
    setIsSelectedAll(false);
    setData(type, updatedData);
    try {
      const payload = {
        documentIds: selectedImgIds,
      };
      await GalleryApi.deleteMedia(payload);
    } catch (error) {
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("GalleryContent.tsx - handleDelete", error);
    }
  };

  const handlePreviewVideo = (videoPath: string) => {
    setVideoPreview(videoPath);
    setIsModalVideoPreview(true);
  };

  let timeout: any;
  const handleOnChange = ({ fileList }: any) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      const filesToUpload = fileList
        .map((file: any) => {
          if (type == "video") {
            if (!Validates.validateVideoFile(file.originFileObj)) {
              Helpers.notification.warning(
                `${file.name} is not an video file!`
              );
              return null;
            }
          } else {
            if (!Validates.validateImageFile(file.originFileObj)) {
              Helpers.notification.warning(
                `${file.name} is not an image file!`
              );
              return null;
            }
            if (!Validates.validateFileSize(file.originFileObj, 5)) {
              Helpers.notification.warning(
                `${file.name} exceeds the 5 MB limit`
              );
              return null;
            }
            return file.originFileObj;
          }
        })
        .filter((file: any) => file != null);
      setPlaceHolderQuantity(filesToUpload.length);
      handleUpload(filesToUpload);
      setTimeout(() => {
        fileList.length = 0;
      }, 0);
    }, 300);
  };

  const handleUpload = async (files: File[]) => {
    if (files.length == 0) return;
    const userImageLength = data.filter(
      (item) => item.is_default == false
    ).length;

    if (files.length > limit || files.length + userImageLength > limit) {
      Helpers.notification.warning(
        `You are allowed to upload up to ${limit} images only. To upload more, please consider upgrading your plan.`
      );
      setPlaceHolderQuantity(0);
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("gallery", file);
    });
    formData.append("gallery_type", type);
    formData.append("gallery_is_crop", "false");
    try {
      const response = await GalleryApi.uploadMedia(formData);
      if (response.status == 200) {
        const newImagesArr = [...data, ...response.data.data];
        setData(type, newImagesArr);
        setPlaceHolderQuantity(0);
      }
    } catch (error) {
      setPlaceHolderQuantity(0);
      Helpers.notification.error("Can not upload image");
      errorLog("GalleryContent.tsx - handleUpload", error);
    }
  };

  return (
    <>
      <GalleryHeader
        type={type}
        checkAll={isSelectedAll}
        setCheckAll={setIsSelectedAll}
        handleDelete={() => {
          if (selectedImg.length > 0) setIsOpenConfirm(true);
        }}
        handleOnChange={handleOnChange}
      />
      <div className="GalleryContent mt-3">
        <Flex gap={12} wrap>
          {data?.map((item, index) => {
            return (
              <div
                className={`img-item ${
                  selectedImg.includes(item) ? "selected" : ""
                }`}
                onClick={() => {
                  if (selectedImg.includes(item)) {
                    handleAddSelected(item, false);
                  } else {
                    handleAddSelected(item, true);
                  }
                }}
                key={index}
              >
                {item.is_default && (
                  <div className="img-label-default">Default</div>
                )}
                {type == "video" ? (
                  <div
                    className="video-wrap"
                    onClick={() => handlePreviewVideo(item.url)}
                  >
                    <div className="video-mask">
                      <EyeOutlined />
                      <span>Preview</span>
                    </div>
                    <video
                      className="video-item"
                      src={item.url}
                      autoPlay={false}
                    />
                  </div>
                ) : (
                  <Image src={item.url} height={130} preview={false} />
                )}
              </div>
            );
          })}
          {Array.from({ length: placeHolderQuantity }).map((_, index) => {
            return (
              <div className="img-item-placeholder" key={index}>
                <div className="loader"></div>
              </div>
            );
          })}
        </Flex>
        <Modal
          className="video-preview-modal"
          centered
          width={1000}
          open={isModalVideoPreview}
          footer={false}
          onCancel={() => {
            setVideoPreview("");
            setIsModalVideoPreview(false);
          }}
        >
          <video
            className="video-preview"
            src={videoPreview}
            autoPlay={true}
            controls
          />
        </Modal>
      </div>
      <Modal
        title="Confirm"
        open={isOpenConfirm}
        onOk={handleDelete}
        onCancel={() => setIsOpenConfirm(false)}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure to delete the selection images?</p>
      </Modal>
    </>
  );
};

export default GalleryContent;
